import { jsx as _jsx } from "react/jsx-runtime";
import { useBreakpointsContext } from "@air/provider-media-query";
import constant from "lodash/constant";
import noop from "lodash/noop";
import { useCallback, useMemo } from "react";
import { GalleryItemType } from "~/components/Gallery/types";
import { UploadingAssetGalleryCard } from "~/components/Gallery/UploadingAssetGalleryCard";
import { useGallerySectionFooter } from "~/components/Gallery/useGallerySectionFooter";
import { useGallerySectionHeader } from "~/components/Gallery/useGallerySectionHeader";
import DragType from "~/components/Shared/Drag/dragTypes";
import { useJustifiedGrid } from "~/components/Shared/JustifiedGrid";
import { WKSPC_DESKTOP_HORIZONTAL_PADDING_WITH_SIDENAV_OPEN } from "~/constants/WorkspaceSpacing";
import { getClipRatio } from "~/hooks/useClipRatios";
export const AssetGalleryCardSize = {
    small: 165,
    medium: 220,
    large: 440,
    "extra-large": 660
};
export const useGalleryAssets = (param)=>{
    let { renderAsset, containerWidth, loadMore = noop, data, uploads = [], itemHeight, isFirstSection, onSectionCollapse, containerHorizontalPadding = WKSPC_DESKTOP_HORIZONTAL_PADDING_WITH_SIDENAV_OPEN, itemAdditionalHeight = 0, isSelectable = constant(false) } = param;
    const { isAboveSmallScreen } = useBreakpointsContext();
    const assets = useMemo(()=>{
        var _data_items;
        return [
            ...uploads.map((u)=>({
                    item: u,
                    type: GalleryItemType.upload
                })),
            ...((_data_items = data === null || data === void 0 ? void 0 : data.items) !== null && _data_items !== void 0 ? _data_items : []).map((u)=>({
                    item: u,
                    type: GalleryItemType.asset
                }))
        ];
    }, [
        data === null || data === void 0 ? void 0 : data.items,
        uploads
    ]);
    var _data_total;
    const total = ((_data_total = data === null || data === void 0 ? void 0 : data.total) !== null && _data_total !== void 0 ? _data_total : 0) + uploads.length;
    const ratios = [
        ...uploads.map((param)=>{
            let { apiUploadData } = param;
            return {
                width: 0,
                height: 0,
                ...apiUploadData
            };
        }),
        ...(data === null || data === void 0 ? void 0 : data.items) || []
    ].map((clip)=>getClipRatio(clip));
    const { boxes: assetBoxes } = useJustifiedGrid({
        containerWidth: containerWidth,
        ratios,
        options: {
            containerPadding: {
                left: containerHorizontalPadding,
                right: containerHorizontalPadding
            },
            targetRowHeight: itemHeight,
            boxSpacing: {
                horizontal: 24
            },
            additionalRowHeight: itemAdditionalHeight
        }
    });
    const renderItem = useCallback((index)=>{
        const asset = assets[index];
        if (asset) {
            return asset.type === GalleryItemType.upload ? /*#__PURE__*/ _jsx(UploadingAssetGalleryCard, {
                width: /**
               * Uploads don't inherently have a width and get it from the ratio of the
               * row height
               */ itemHeight,
                upload: asset.item
            }) : renderAsset === null || renderAsset === void 0 ? void 0 : renderAsset({
                box: assetBoxes[index],
                data: asset.item,
                index
            });
        }
        return null;
    }, [
        assets,
        itemHeight,
        renderAsset,
        assetBoxes
    ]);
    const verticalSpacing = !isAboveSmallScreen ? 12 : 24;
    const getItemData = useCallback((index)=>({
            type: assets[index].type,
            isSelectable: assets[index].type === GalleryItemType.asset ? isSelectable(assets[index].item) : false,
            itemId: assets[index].item.id,
            canDragTo: (item)=>item.type === DragType.asset
        }), [
        assets,
        isSelectable
    ]);
    const { isHeaderCollapsed, header } = useGallerySectionHeader({
        isFirstSection,
        title: "Assets (".concat(total.toLocaleString(), ")"),
        containerWidth,
        containerPadding: containerHorizontalPadding,
        onSectionCollapse
    });
    const footer = useGallerySectionFooter({
        isLoading: !!(data === null || data === void 0 ? void 0 : data.isLoading),
        loadMore,
        hasMore: !!(data === null || data === void 0 ? void 0 : data.hasMore),
        containerWidth
    });
    if (!total && !(data === null || data === void 0 ? void 0 : data.isLoading)) {
        return null;
    }
    return {
        header: !!total ? header : undefined,
        footer: isHeaderCollapsed ? undefined : footer,
        items: {
            render: renderItem,
            boxes: isHeaderCollapsed ? [] : assetBoxes,
            verticalItemsSpacing: verticalSpacing,
            getData: getItemData
        }
    };
};
